/**
 * Ставки расчёта своих сил компании (решение 0.4 ТЗ v2.0): начисления на ФОТ, накладные и непредвиденные. Новый
 * вариант исполнения берёт их, если ставка в нём не задана; у сохранённых вариантов ставки уже записаны.
 */
import { ESTIMATE_RATES_DEFAULTS, type EstimateRates, type EstimateRatesView } from '@epc/contracts';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { ErrorBox, toast } from '../../ui/feedback';
import { Field, Input } from '../../ui/form';
import { Panel } from '../../ui/layout';

export const ratesQuery = queryOptions({
  queryKey: ['budget', 'rates'],
  queryFn: () => api<EstimateRatesView>('/budget/rates'),
  staleTime: 60_000,
});

/** Ставки компании для подсказок формы; пока не загрузились — значения по умолчанию */
export function useRates(): EstimateRates {
  return useQuery(ratesQuery).data ?? ESTIMATE_RATES_DEFAULTS;
}

export const comma = (v: number) => String(v).replace('.', ',');
// Неразрывный пробел: «10 %» не разрывается на две строки
const pct = (v: number) => `${comma(v)}\u00a0%`;

function RatesDialog({ rates, onClose }: { rates: EstimateRates; onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    taxesPct: comma(rates.taxesPct),
    overheadPct: comma(rates.overheadPct),
    contingencyPct: comma(rates.contingencyPct),
  });
  const parsed = Object.fromEntries(
    Object.entries(form).map(([k, v]) => [k, Number(v.replace(',', '.').trim())]),
  ) as EstimateRates;
  const valid =
    Object.values(form).every((v) => v.trim() !== '') &&
    Object.values(parsed).every((v) => Number.isFinite(v) && v >= 0 && v <= 100);
  const m = useMutation({
    mutationFn: () => api<EstimateRatesView>('/budget/rates', { method: 'PUT', body: parsed }),
    onSuccess: (v) => {
      qc.setQueryData(ratesQuery.queryKey, v);
      toast('Ставки компании сохранены');
      onClose();
    },
  });
  const field = (key: keyof EstimateRates, label: string) => (
    <Field label={label}>
      <Input
        inputMode="decimal"
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
      />
    </Field>
  );
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title="Ставки компании"
      description="Для новых вариантов исполнения «свои силы»; уже сохранённые варианты не пересчитываются"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="primary" loading={m.isPending} disabled={!valid} onClick={() => m.mutate()}>
            Сохранить
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-3">
        {field('taxesPct', 'Начисления на ФОТ, %')}
        {field('overheadPct', 'Накладные, %')}
        {field('contingencyPct', 'Непредвиденные, %')}
      </div>
      {!valid ? <p className="mt-3 text-[14px] text-danger">Каждая ставка — число от 0 до 100.</p> : null}
      <ErrorBox error={m.error} className="mt-4" />
    </Dialog>
  );
}

export function RatesPanel() {
  const q = useQuery(ratesQuery);
  const [edit, setEdit] = useState(false);
  if (!q.data) return q.isError ? <ErrorBox error={q.error} /> : null;
  const r = q.data;
  return (
    <Panel
      title="Ставки компании"
      meta="для расчёта своих сил в вариантах исполнения"
      actions={
        r.canEdit ? (
          <Button variant="secondary" onClick={() => setEdit(true)}>
            Изменить
          </Button>
        ) : null
      }
    >
      <p className="num text-[14px]">
        Начисления на ФОТ {pct(r.taxesPct)} · накладные {pct(r.overheadPct)} · непредвиденные{' '}
        {pct(r.contingencyPct)}
      </p>
      <p className="mt-1 text-[13px] text-ink-2">
        {r.isDefault
          ? 'Компания своих ставок не задавала — действуют значения по умолчанию.'
          : 'Ставки заданы компанией.'}{' '}
        Ставка, введённая в самом варианте, важнее; сохранённые варианты от смены ставок не меняются.
      </p>
      {edit ? <RatesDialog rates={r} onClose={() => setEdit(false)} /> : null}
    </Panel>
  );
}
